import { OfferSubCategory } from '../../../common/enums.js';
import { useCheckoutContext } from '../context/CheckoutContextCoCo.js';
import {
  BundleAddonGroupRuntimeCharacteristic,
  OrderParamExtensions,
  PlanProductRuntimeCharacteristic,
  getAddonOfferings,
  getRuntimeCharacteristics,
  isFixedMobileBroadband,
  isPhysicalSimOffering,
  supportsExistingSim,
} from '../utils/characteristicsFlagsUtilsCoCo.js';
import { isProductSubcategory } from '../utils/productSubcategoryUtilsCoCo.js';
import type { ProductOfferingsResponse } from '../../../generated/api/productOfferingsResponse.js';

export interface ProductConfigSchema {
  needsContact: boolean;
  needsCostCenter: boolean;
  needsReference: boolean;
  needsPhoneNumber: boolean;
  needsEsim: boolean;
  needsPsim: boolean;
  needsActiveSim: boolean;
  needsInstallationContact: boolean;
  needsNumberPublicity: boolean;
}

export const initialConfigState: ProductConfigSchema = {
  needsContact: false,
  needsCostCenter: false,
  needsReference: false,
  needsPhoneNumber: false,
  needsEsim: false,
  needsPsim: false,
  needsActiveSim: false,
  needsInstallationContact: false,
  needsNumberPublicity: false,
};

export const NOT_TO_DIRECTORY_VALUE = 'Not to directory';

export const deriveConfigSchema = (offer?: ProductOfferingsResponse): ProductConfigSchema => {
  if (!offer) {
    return initialConfigState;
  }

  const runtimeChars = getRuntimeCharacteristics(offer);
  const extensions = offer.extensions ?? [];
  const subcategory = offer.productOfferingType?.subcategory;

  const isMobileBroadband = isProductSubcategory(subcategory, OfferSubCategory.MOBILE_BROADBAND);

  const hasChar = (name: string) => runtimeChars.some(c => c.name === name);
  const hasExt = (name: string) => extensions.some(e => e.name === name);

  const allAddonOfferings = getAddonOfferings(offer);
  const esimAddon = allAddonOfferings.find(
    o => o.name?.value?.toLowerCase() === BundleAddonGroupRuntimeCharacteristic.ESIM
  );
  const psimAddon = allAddonOfferings.find(isPhysicalSimOffering);

  const needsEsim = !!esimAddon;
  const needsPsim = !!psimAddon;
  const needsActiveSim = !!psimAddon && supportsExistingSim(offer, psimAddon);

  const isVoiceOrMBB = isProductSubcategory(subcategory, OfferSubCategory.MOBILE_VOICE) || isMobileBroadband;
  const needsPhoneNumber =
    hasChar(PlanProductRuntimeCharacteristic.MSISDN) &&
    (needsEsim || needsPsim || isVoiceOrMBB) &&
    !isFixedMobileBroadband(offer);
  const needsInstallationContact =
    isProductSubcategory(subcategory, OfferSubCategory.FIXED_MOBILE_BROADBAND) ||
    isProductSubcategory(subcategory, OfferSubCategory.FIXED_BROADBAND);

  return {
    needsContact: hasChar(PlanProductRuntimeCharacteristic.PURPOSE_OF_USE),
    needsCostCenter: hasExt(OrderParamExtensions.SUBSCRIPTION_COST_CENTER),
    needsReference: hasExt(OrderParamExtensions.SUBSCRIPTION_REFERENCE),
    needsPhoneNumber,
    needsEsim,
    needsPsim,
    needsActiveSim,
    needsInstallationContact,
    needsNumberPublicity: hasChar(PlanProductRuntimeCharacteristic.DIRECTORY_LISTING_PUBLICITY) && !isMobileBroadband,
  };
};

export const useProductConfigSchemaCoCo = (basketItemId: string): ProductConfigSchema => {
  const { selectedProductOffers } = useCheckoutContext();
  const offer = selectedProductOffers.find(o => o.id === basketItemId);
  return deriveConfigSchema(offer);
};

export const schemaToCharacteristicFlags = (schema: ProductConfigSchema) => ({
  fieldsToShow: {
    showPurposeOfUse: schema.needsContact,
    showCostCenter: schema.needsCostCenter,
    showReference: schema.needsReference,
    showNumberPublicity: schema.needsNumberPublicity,
  },
});

export const hasConnectivityConfig = (schema: ProductConfigSchema): boolean =>
  schema.needsPhoneNumber ||
  schema.needsEsim ||
  schema.needsPsim ||
  schema.needsActiveSim ||
  schema.needsNumberPublicity;
